import { ComposedSurveyPlugin, ResponseRecord } from "@hyper-inferno/survey-elements-core";

/**
 * Extracts the body of a JavaScript function as a raw string.
 * Strips away function signatures, argument definitions, and outer brackets.
 */
function stringifyFunctionBody(fn: Function | string | undefined): string | undefined {
  if (!fn) return undefined;
  if (typeof fn === 'string') return fn;

  const fnStr = fn.toString().trim();

  // Finds the first opening bracket '{' and the last closing bracket '}'
  const firstBracket = fnStr.indexOf('{');
  const lastBracket = fnStr.lastIndexOf('}');

  if (firstBracket !== -1 && lastBracket !== -1) {
    // Returns only the executable code inside the brackets
    return fnStr.slice(firstBracket + 1, lastBracket).trim();
  }

  return fnStr;
}

/**
 * Transforms a hardcoded plugin into a ComposedSurveyPlugin,
 * converting all hook functions into raw execution strings.
 */
export function transformToComposedPlugin(rawPlugin: any, answerObject: ResponseRecord): ComposedSurveyPlugin {
  if (!rawPlugin || typeof rawPlugin !== 'object') {
    throw new Error("Transformation failed: Invalid raw plugin object.");
  }

  return {
    id: rawPlugin.id,
    name: rawPlugin.name,
    usageId: rawPlugin.usageId,

    context: {
      type: rawPlugin.context?.type ?? 'global',
      surveyId: rawPlugin.context?.surveyId ?? null,
      pageId: rawPlugin.context?.pageId ?? null,
      elementId: rawPlugin.context?.elementId ?? null,
    },

    instanceValues: rawPlugin.instanceValues ?? {},
    variable_schema: rawPlugin.variable_schema ?? { type: "object", properties: {} },

    // Converts JavaScript functions into evaluation-ready strings
    hooks: {
      onPageInit: stringifyFunctionBody(rawPlugin.hooks?.onPageInit),
      onDataChange: stringifyFunctionBody(rawPlugin.hooks?.onDataChange),
      onBeforeSubmit: stringifyFunctionBody(rawPlugin.hooks?.onBeforeSubmit),
      onCleanup: stringifyFunctionBody(rawPlugin.hooks?.onCleanup),
    } as any, // Casted to any to bypass strict type checking if your type definition expects functions
    answerObject,

    ...(rawPlugin.html && { html: rawPlugin.html })
  };
}